import React, { useState, useEffect, useRef } from 'react';
import { Upload, Image as ImageIcon, X, Loader2, Check } from 'lucide-react';
import { adminApi, absoluteMediaUrl } from './api';
import { useLanguage } from '../context/LanguageContext';
import { ADMIN_T } from './labels';

/**
 * A compact media picker that:
 *  - lets you upload a file (multipart),
 *  - paste an external URL,
 *  - or browse already-uploaded library assets.
 *
 * Returns the chosen URL via `onSelect(url)`.
 */
export const MediaPicker = ({ open, onClose, onSelect, accept = 'image/*' }) => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = ADMIN_T[language] || ADMIN_T.en;
  const [tab, setTab] = useState('library');
  const [library, setLibrary] = useState([]);
  const [pasted, setPasted] = useState('');
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const params = {};
    if (accept.startsWith('image')) params.mime = 'image';
    if (search) params.search = search;
    adminApi.get('/media', { params }).then(({ data }) => setLibrary(data)).catch(() => {});
  }, [open, search, accept]);

  if (!open) return null;

  const handleFile = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    try {
      const { data } = await adminApi.post('/media', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSelect(absoluteMediaUrl(data.url));
      onClose();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      dir={isAr ? 'rtl' : 'ltr'}
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-3xl max-h-[85vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden">
        <div className={`flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-200 dark:border-slate-800 ${isAr ? 'flex-row-reverse' : ''}`}>
          <h3 className="text-base font-bold text-slate-900 dark:text-white">{t.media}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className={`flex gap-1 p-2 border-b border-slate-200 dark:border-slate-800 ${isAr ? 'flex-row-reverse' : ''}`}>
          {[
            ['library', t.pickFromLibrary],
            ['upload', t.uploadFile],
            ['url', t.pasteUrl],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                tab === key
                  ? 'bg-brand-blue/10 text-brand-blue-700 dark:text-cyan-300'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'library' && (
            <>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.search}
                className="w-full mb-4 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm"
              />
              {library.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-10">{t.noData}</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {library.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => { onSelect(absoluteMediaUrl(m.url)); onClose(); }}
                      className="group relative aspect-square rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-brand-blue hover:shadow-md transition"
                      title={m.original_name}
                    >
                      {m.mime_type.startsWith('image/') ? (
                        <img src={absoluteMediaUrl(m.url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-slate-100 dark:bg-slate-800 text-xs text-slate-600 dark:text-slate-300 p-2 text-center">
                          <ImageIcon className="w-6 h-6 mb-1 opacity-50" />
                          <span className="truncate w-full">{m.original_name}</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === 'upload' && (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <input
                ref={inputRef}
                type="file"
                accept={accept}
                hidden
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow hover:shadow-lg disabled:opacity-60"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? '…' : t.chooseFile}
              </button>
              <p className="text-xs text-slate-500 mt-3">JPG / PNG / WEBP / PDF — max 25 MB</p>
            </div>
          )}

          {tab === 'url' && (
            <div className="space-y-3">
              <input
                type="url"
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                placeholder="https://…"
                dir="ltr"
                className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm"
              />
              <button
                type="button"
                disabled={!pasted}
                onClick={() => { onSelect(pasted.trim()); onClose(); }}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold disabled:opacity-50"
              >
                <Check className="w-4 h-4" /> {t.add}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaPicker;
